import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
  viewChild,
} from '@angular/core';

import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  getLastIndexOfArray,
  Guid,
  isNil,
  isNotNil,
  Nillable,
  valueOrDefault,
} from '@web/utils';
import {
  ArrayValidator,
  FormControlComponent,
  PrimengButtonDirective,
  PrimengTextareaDirective,
} from '@web/shared/ui';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { Accordion } from 'primeng/accordion';
import { DatePicker } from 'primeng/datepicker';
import { WarehouseFilesUploadComponent } from '../../containers/warehouse-files-upload/warehouse-files-upload.component';
import { WarehouseReceiptFormData } from '../../models/warehouse-receipt-form-data';
import {
  WarehouseReleaseFormData,
  WarehouseReleaseFormGroup,
  WarehouseReleaseProductFormData,
  WarehouseReleaseProductFormGroup,
} from '../../models/warehouse-release-form-data';
import { WarehouseReleaseFormProductPanelComponent } from '../warehouse-release-form-product-panel/warehouse-release-form-product-panel.component';

@Component({
  selector: 'app-warehouse-release-form',
  imports: [
    Accordion,
    ButtonModule,
    DatePicker,
    FormControlComponent,
    InputTextModule,
    PrimengButtonDirective,
    PrimengTextareaDirective,
    ReactiveFormsModule,
    Textarea,
    WarehouseFilesUploadComponent,
    WarehouseReleaseFormProductPanelComponent,
  ],
  templateUrl: './warehouse-release-form.component.html',
  styleUrl: './warehouse-release-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseReleaseFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);

  public readonly data = input<Nillable<WarehouseReleaseFormData>>();
  public readonly submitSuccessEvent = output<WarehouseReleaseFormData>();
  public readonly fileUploadVisibility = input(false);

  protected readonly accordion = viewChild(Accordion);
  protected readonly form = this.buildForm();
  protected readonly valueOrDefault = valueOrDefault;
  protected readonly isNotNil = isNotNil;

  protected activeProductIndex = 0;

  public ngOnInit(): void {
    this.fillByData();
  }

  public handleSubmitClick(): void {
    const data = this.form.getRawValue();

    this.submitSuccessEvent.emit(
      new WarehouseReleaseFormData({
        comment: data.comment,
        releasedAt: data.releasedAt,
        products: data.products.map(
          (product) =>
            new WarehouseReleaseProductFormData({
              productId: product.id!,
              quantity: product.quantity!,
            }),
        ),
        fileIds: data.fileIds,
      }),
    );
  }

  public getProductFormGroups(): WarehouseReleaseProductFormGroup[] {
    return this.form.controls.products.controls;
  }

  public handleProductRemoveClick(index: number): void {
    this.form.controls.products.removeAt(index);
    this.activeProductIndex = this.form.controls.products.length - 1;
  }

  public handleProductAddClick(): void {
    this.form.controls.products.push(this.buildProductFormGroup());
    this.activeProductIndex = this.form.controls.products.length - 1;
  }

  private buildForm(): WarehouseReleaseFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      releasedAt: this.builder.nonNullable.control(new Date(), [Validators.required]),
      comment: this.builder.control<Nillable<string>>(null, [Validators.maxLength(WarehouseReleaseFormData.MaxCommentLength)]),
      products: this.builder.array<WarehouseReleaseProductFormGroup>([], [ArrayValidator.min(WarehouseReceiptFormData.MinProductCount)]),
      fileIds: this.builder.control<Nillable<Guid[]>>(null)
    })
  }

  private buildProductFormGroup(
    data?: WarehouseReleaseProductFormData,
  ): WarehouseReleaseProductFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      id: this.builder.control<Nillable<Guid>>(data?.productId, [Validators.required]),
      quantity: this.builder.control<Nillable<number>>(data?.quantity, [Validators.required]),
    })
  }

  private fillByData(): void {
    const data = this.data();

    if (isNil(data)) {
      this.form.controls.products.push(this.buildProductFormGroup());

      return;
    }

    const products = valueOrDefault(data.products, []);

    products.forEach((product) =>
      this.form.controls.products.push(this.buildProductFormGroup(product)),
    );

    this.activeProductIndex = getLastIndexOfArray(products);

    this.form.patchValue(
      {
        comment: data?.comment,
        releasedAt: data?.releasedAt,
        fileIds: data?.fileIds,
      },
      { emitEvent: false },
    );
  }
}
